import { read, write, STORAGE_KEYS as KEYS } from "./storage";
import { getVendorById, saveVendor } from "./businessRepository";
import { DEFAULT_PLAN_MATRIX, PLAN_TIERS, getPlanConfig, getPlanMatrix, normalizePlan, resetPlanMatrix, savePlanMatrix } from "./modelDefaults";
import { APP_CONFIG } from "@/config/appConfig";

export const PLAN_STORAGE_KEY = KEYS.planMatrix;
export const PERSONA_STORAGE_KEY = KEYS.personaMatrix;
export const DEFAULT_LANDING_CONFIG = {
  brand: { name: APP_CONFIG.brandName, logoUrl: "" },
  navigation: {
    items: [],
    loginLabel: "Login", loginTo: "/login",
    ctaLabel: "Create free storefront", ctaTo: "/register",
  },
  hero: {
    eyebrow: "",
    headlineLine1: "Take your business online.",
    headlineLine2: "",
    headlineHighlight: "Grow on your terms.",
    headlineLine3: "",
    subtitle: "Launch your storefront, share QR, manage orders, and track sales.",
    ctaPrimary: "Create free storefront",
    ctaPrimaryTo: "/register",
    ctaSecondary: "Explore the 5-steps setup",
    ctaSecondaryTo: "#how-it-works",
    flowSteps: ["Add your menu", "Share QR/link", "Receive orders"],
    socialProof: "200+ stores onboarded",
  },
  howItWorks: {
    eyebrow: "How it works",
    steps: [
      { icon: "create", title: "Create StoreFront", body: "Add basic details." },
      { icon: "menu", title: "Manage Menu", body: "Add items, prices and photos." },
      { icon: "share", title: "Download/Share QR", body: "Share it on WhatsApp, Instagram or print QR." },
      { icon: "orders", title: "Manage orders", body: "Create/Monitor orders" },
      { icon: "sales", title: "Track Sales", body: "Watch real-time insights." },
    ],
  },
  businessTypes: {
    eyebrow: "Business",
    availableLabel: "Available now",
    comingLabel: "Coming later",
    available: [
      { id: "restaurants", name: "Restaurants", icon: "restaurant", visible: true },
      { id: "cafes", name: "Cafés", icon: "cafe", visible: true },
    ],
    coming: [
      { id: "salons", name: "Salons", icon: "salon", visible: true },
      { id: "bakeries", name: "Bakeries", icon: "bakery", visible: true },
      { id: "gyms", name: "Gyms", icon: "gym", visible: true },
      { id: "clinics", name: "Clinics", icon: "clinic", visible: true },
      { id: "academies", name: "Academies", icon: "academy", visible: true },
      { id: "boutiques", name: "Boutiques", icon: "boutique", visible: true },
      { id: "spas", name: "Spas", icon: "spa", visible: true },
      { id: "grocery", name: "Grocery", icon: "grocery", visible: true },
      { id: "rental-property", name: "Rental & Property", icon: "property", visible: true },
    ],
  },
  faqSection: {
    eyebrow: "FAQ",
    title: "Frequently Asked Questions",
  },
  finalCta: {
    title: "Take your business online in minutes.",
    primaryLabel: "Create free storefront",
    primaryTo: "/register",
    secondaryLabel: "See demo",
    secondaryTo: "/discover",
  },
  footer: {
    links: [
      { id: "business-login", label: "Login", to: "/login", visible: true },
      { id: "privacy", label: "Privacy", to: "/privacy", visible: true },
      { id: "terms", label: "Terms", to: "/terms", visible: true },
    ],
    copyrightSuffix: "Built for local businesses.",
  },
  legal: {
    privacy: {
      title: "Privacy Policy",
      paragraphs: [
        "Digital Indian Market uses the business and order information provided through the platform to operate storefronts, process direct orders and provide support.",
        "Businesses are responsible for collecting only the customer information they need and for using it appropriately. We do not sell customer order information.",
        "For privacy questions or requests, use the Support option on the Digital Indian Market website.",
      ],
    },
    terms: {
      title: "Terms of Use",
      paragraphs: [
        "By using Digital Indian Market, you agree to provide accurate business information and use the platform only for lawful business activity.",
        "Businesses remain responsible for their menus, prices, availability, customer service, orders and compliance obligations.",
        "Plan availability and features may change as the platform develops. Contact support for current plan details.",
      ],
    },
  },
  faqs: [
    { q: "Is Digital Indian Market free to start?", a: "Yes. You can create your business and start on the free plan." },
    { q: "Do you charge commission on orders?", a: "No. Digital Indian Market does not take commission from your orders." },
    { q: "Do customers need to create an account?", a: "No. Customers can open your menu and place an order without signing up." },
    { q: "How do customers place an order?", a: "Share your QR code or business link. Customers open the menu, choose items and place their order directly." },
    { q: "Can I update my menu anytime?", a: "Yes. You can change items, prices, photos and availability from your dashboard." },
    { q: "What business types do you support?", a: "Restaurants and cafés are available first, with more local business types coming over time." },
  ],
};

export function updateVendorFeatures(vendorId, patch) {
  const v = getVendorById(vendorId);
  if (!v) return null;
  const features = { ...v.features, ...patch };
  saveVendor({ ...v, features });
  return features;
}
export function updateVendorPlan(vendorId, plan) {
  const v = getVendorById(vendorId);
  if (!v) return null;
  const normalized = normalizePlan(plan);
  const cfg = getPlanConfig(normalized);
  const features = {
    multiStore: cfg.multiStore,
  };
  saveVendor({ ...v, plan: normalized, features });
  return { plan: normalized, features };
}
export const PERSONAS = ["owner", "customer"];

export const FEATURE_CATALOG = [
  { id: "overview",         label: "Dashboard overview" },
  { id: "orders",           label: "Orders board" },
  { id: "bookings",         label: "Bookings board" },
  { id: "catalogue",        label: "Catalogue editor" },
  { id: "pos",              label: "POS (walk-in orders)" },
  { id: "insights",         label: "Business insights" },
  { id: "storefronts",      label: "Storefronts management" },
  { id: "settings",         label: "Store settings" },
  { id: "profile",          label: "My profile" },
  { id: "customer_profile", label: "Customer profile" },
  { id: "chat_support",     label: "Chat support widget" },
];

export const DEFAULT_PERSONA_MATRIX = {
  overview:         ["owner"],
  orders:           ["owner"],
  bookings:         ["owner"],
  catalogue:        ["owner"],
  pos:              ["owner"],
  insights:         ["owner"],
  storefronts:      ["owner"],
  settings:         ["owner"],
  profile:          ["owner"],
  customer_profile: ["customer"],
  chat_support:     ["customer"],
};

export function getPersonaMatrix() {
  const saved = read(KEYS.personaMatrix, null);
  if (!saved) return DEFAULT_PERSONA_MATRIX;
  return { ...DEFAULT_PERSONA_MATRIX, ...saved };
}
export function savePersonaMatrix(m) {
  write(KEYS.personaMatrix, m);
  return m;
}
export function resetPersonaMatrix() {
  localStorage.removeItem(KEYS.personaMatrix);
  return DEFAULT_PERSONA_MATRIX;
}
export function isFeatureVisibleForPersona(featureId, persona) {
  const m = getPersonaMatrix();
  return (m[featureId] || []).includes(persona);
}
export { DEFAULT_PLAN_MATRIX, PLAN_TIERS, getPlanConfig, getPlanMatrix, resetPlanMatrix, savePlanMatrix };
